<script setup>

    import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
    import { useRoute } from 'vue-router'

    import LatteNotesActivity from '@/Components/Notes/LatteNotesActivity.vue'
    import Loader from '@/Components/App/Loader.vue'

    import { IconQuestionCircle12Filled } from '@iconify-prerendered/vue-fluent'

    import { notesSubject, useClientSingleStore, useJobStore } from '@/Stores'

    /**
     * The Notes & Activity module of an existing client on its own for the Coffee iframe. The /embedded/ URL turns on
     * embed mode (App.vue), which drops the app chrome. The create flow stays on Client.vue.
     */

    const NOT_FOUND = 404

    const route = useRoute()
    const clientSingleStore = useClientSingleStore()
    const jobStore = useJobStore()

    const coffeeUserProfileId = computed(() => route.params.coffee_client_user_profile_id)

    /**
     * The Latte client id, resolved from the Coffee profile id in the URL.
     */
    const clientId = ref(null)
    const clientNotesSubject = computed(() => notesSubject(clientId.value))

    /**
     * 'loading' until the client is resolved, then 'ready', 'not-found' or 'error'.
     */
    const status = ref('loading')
    const notesRef = ref(null)

    /**
     * Hydrates the client's jobs into the job store for the composer's caregiver feedback dropdown, as Client.vue does.
     */
    function loadJobs(id) {
        jobStore.index(null, null, null, true, id, null)
            .then(response => jobStore.hydrate(response.data, { start: 14, end: 1, includeMissingStartDate: true }))
            .catch(() => {})
    }

    /**
     * Resolves the Coffee profile id to the Latte client like the other embedded client pages; the module then loads
     * its own timeline and summary.
     */
    function loadClient(coffeeId) {
        status.value = 'loading'

        return clientSingleStore.getClientByCoffeeUserProfileId(coffeeId)
            .then(response => {
                clientId.value = response.data.id
                loadJobs(clientId.value)
                status.value = 'ready'
            })
            .catch(({ error } = {}) => {
                status.value = error?.response?.status === NOT_FOUND ? 'not-found' : 'error'
            })
    }

    watch(coffeeUserProfileId, (newCoffeeId) => {
        if (newCoffeeId) {
            loadClient(newCoffeeId)
        }
    })

    onMounted(() => {
        loadClient(coffeeUserProfileId.value)
    })

    onUnmounted(() => {
        jobStore.purge()
        clientSingleStore.reset()
    })

</script>

<template>

    <div class="flex h-screen flex-col bg-white font-sans">

        <div
            v-if="status === 'loading'"
            class="flex flex-1 items-center justify-center"
        >
            <Loader />
        </div>

        <p
            v-else-if="status === 'not-found'"
            data-test="client-not-found"
            class="m-auto px-4 text-center text-sm text-zinc-500"
        >
            Client not found.
        </p>

        <p
            v-else-if="status === 'error'"
            data-test="client-load-error"
            class="m-auto px-4 text-center text-sm text-zinc-500"
        >
            This client could not be loaded. Please refresh the page.
        </p>

        <template v-else>

            <header class="flex shrink-0 items-center justify-between bg-lifeworx-blue-700 px-4 py-2 border-b border-lifeworx-blue-600">
                <h1 class="text-white text-sm md:text-base font-semibold drop-shadow tracking-wide select-none">
                    Notes &amp; Activity
                </h1>
                <button
                    type="button"
                    data-test="notes-tour"
                    @click="notesRef && notesRef.startTour()"
                    class="rounded-full p-1.5 hover:bg-white/20 transition-colors"
                    title="Start Notes & Activity tour"
                >
                    <IconQuestionCircle12Filled class="size-5 text-white" />
                </button>
            </header>

            <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                <LatteNotesActivity
                    ref="notesRef"
                    :subject="clientNotesSubject"
                    :fill-height="true"
                />
            </div>

        </template>

    </div>

</template>
